import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type {
  CreateItemInput,
  UpdateItemInput,
  ItemResponse,
  PaginatedResponse,
  Category,
} from '@collector/shared'
import * as itemsApi from '@/api/items'
import type { ApiError } from '@/api/client'
import type { GetItemsOptions } from '@/api/items'
import type { LocalItemState } from '@/types/offline'

type SortField = 'createdAt' | 'artistOrManufacturer' | 'location' | 'category'
type SortOrder = 'asc' | 'desc'

export const useItemStore = defineStore('items', () => {
  const items = ref<LocalItemState[]>([])
  const loading = ref(false)
  const error = ref<ApiError | null>(null)
  const pagination = ref<PaginatedResponse<ItemResponse>['pagination'] | null>(null)
  const isLoadingMore = ref(false)

  // Search state
  const searchQuery = ref('')

  function setSearch(query: string) {
    searchQuery.value = query
  }

  function clearSearch() {
    searchQuery.value = ''
  }

  // Filter state
  const filters = ref<{
    category: Category | null
    isVirtual: boolean | null
    tag: string | null
  }>({
    category: null,
    isVirtual: null,
    tag: null,
  })

  const activeFilterCount = computed(() => {
    let count = 0
    if (filters.value.category !== null) count++
    if (filters.value.isVirtual !== null) count++
    if (filters.value.tag !== null) count++
    return count
  })

  function setFilter(
    key: 'category' | 'isVirtual' | 'tag',
    value: Category | boolean | string | null
  ) {
    if (key === 'category') {
      filters.value.category = value as Category | null
    } else if (key === 'isVirtual') {
      filters.value.isVirtual = value as boolean | null
    } else {
      filters.value.tag = value as string | null
    }
    localStorage.setItem('gallery-filters', JSON.stringify(filters.value))
  }

  function clearFilters() {
    filters.value = { category: null, isVirtual: null, tag: null }
    localStorage.removeItem('gallery-filters')
  }

  function loadPersistedFilters() {
    const saved = localStorage.getItem('gallery-filters')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        filters.value = {
          category: parsed.category ?? null,
          isVirtual: parsed.isVirtual ?? null,
          tag: parsed.tag ?? null,
        }
      } catch {
        // Invalid JSON, ignore
      }
    }
  }

  // Sort state
  const sort = ref<{ sortBy: SortField; sortOrder: SortOrder }>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
  })

  function setSort(sortBy: SortField, sortOrder: SortOrder) {
    sort.value = { sortBy, sortOrder }
    localStorage.setItem('gallery-sort', JSON.stringify(sort.value))
  }

  function clearSort() {
    sort.value = { sortBy: 'createdAt', sortOrder: 'desc' }
    localStorage.removeItem('gallery-sort')
  }

  function loadPersistedSort() {
    const saved = localStorage.getItem('gallery-sort')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (parsed.sortBy && parsed.sortOrder) {
          sort.value = parsed
        }
      } catch {
        // Invalid JSON, use defaults
      }
    }
  }

  const hasMore = computed(() => {
    if (!pagination.value) return false
    return pagination.value.page < pagination.value.totalPages
  })

  async function createItem(data: CreateItemInput): Promise<ItemResponse> {
    loading.value = true
    error.value = null
    try {
      const item = await itemsApi.createItem(data)
      items.value.unshift(item)
      return item
    } catch (err) {
      error.value = err as ApiError
      throw err
    } finally {
      loading.value = false
    }
  }

  async function uploadImage(itemId: number, file: File): Promise<ItemResponse> {
    loading.value = true
    error.value = null
    try {
      const updatedItem = await itemsApi.uploadItemImage(itemId, file)
      const index = items.value.findIndex((i) => i.id === itemId)
      if (index !== -1) {
        items.value[index] = updatedItem
      }
      return updatedItem
    } catch (err) {
      error.value = err as ApiError
      throw err
    } finally {
      loading.value = false
    }
  }

  async function getItemById(id: number): Promise<ItemResponse> {
    loading.value = true
    error.value = null
    try {
      const item = await itemsApi.getItem(id)
      return item
    } catch (err) {
      error.value = err as ApiError
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateItem(id: number, data: UpdateItemInput): Promise<ItemResponse> {
    loading.value = true
    error.value = null
    try {
      const updatedItem = await itemsApi.updateItem(id, data)
      const index = items.value.findIndex((i) => i.id === id)
      if (index !== -1) {
        items.value[index] = updatedItem
      }
      return updatedItem
    } catch (err) {
      error.value = err as ApiError
      throw err
    } finally {
      loading.value = false
    }
  }

  async function deleteItem(id: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      await itemsApi.deleteItem(id)
      const index = items.value.findIndex((i) => i.id === id)
      if (index !== -1) {
        items.value.splice(index, 1)
      }
    } catch (err) {
      error.value = err as ApiError
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchItems(
    options?: GetItemsOptions & { append?: boolean }
  ): Promise<PaginatedResponse<ItemResponse>> {
    if (options?.append) {
      isLoadingMore.value = true
    } else {
      loading.value = true
    }
    error.value = null
    try {
      const apiOptions: GetItemsOptions = {}
      if (options?.page) apiOptions.page = options.page
      if (options?.limit) apiOptions.limit = options.limit
      if (filters.value.category !== null) apiOptions.category = filters.value.category
      if (filters.value.isVirtual !== null) apiOptions.isVirtual = filters.value.isVirtual
      if (filters.value.tag !== null) apiOptions.tag = filters.value.tag
      if (searchQuery.value.length >= 2) apiOptions.search = searchQuery.value
      apiOptions.sortBy = sort.value.sortBy
      apiOptions.sortOrder = sort.value.sortOrder
      const response = await itemsApi.getItems(
        Object.keys(apiOptions).length ? apiOptions : undefined
      )
      if (options?.append) {
        items.value = [...items.value, ...response.data]
      } else {
        items.value = response.data
      }
      pagination.value = response.pagination
      return response
    } catch (err) {
      error.value = err as ApiError
      throw err
    } finally {
      loading.value = false
      isLoadingMore.value = false
    }
  }

  async function fetchMoreItems(): Promise<PaginatedResponse<ItemResponse> | undefined> {
    if (!hasMore.value || isLoadingMore.value) return
    const nextPage = (pagination.value?.page ?? 0) + 1
    const limit = pagination.value?.limit ?? 10
    return fetchItems({ page: nextPage, limit, append: true })
  }

  return {
    items,
    loading,
    error,
    pagination,
    isLoadingMore,
    hasMore,
    searchQuery,
    setSearch,
    clearSearch,
    filters,
    activeFilterCount,
    createItem,
    uploadImage,
    getItemById,
    updateItem,
    deleteItem,
    fetchItems,
    fetchMoreItems,
    setFilter,
    clearFilters,
    loadPersistedFilters,
    sort,
    setSort,
    clearSort,
    loadPersistedSort,
  }
})
